import { Button, useRect } from '@platform/ui-library';
import {
  FC,
  useEffect,
  useMemo,
  useState
} from 'react';
import { languageSpeakDatesAsOptions } from '@app/const';
import {
  Direction,
  PopUp,
  PopUpProps,
  Select
} from '@app/form_components';
import { useAsync, useServices } from '@app/hooks';
import {
  AudioType,
  DesignLanguage,
  LanguageCode,
  LanguageSpeakDatesAs,
  TextToSpeechVoice,
  Voice
} from '@app/types';
import styles from '@app/styles/components/modal.module.scss';

export const languageTypeLabels: Record<AudioType | 'language' | 'ttsDateFormat', string> = {
  language: 'Language',
  ttsDateFormat: 'TTS Date',
  [AudioType.tts]: 'TTS Voice',
  [AudioType.ttp]: 'Voice Talent'
};

const designLanguageSeed: DesignLanguage = {
  // Start by selecting the second most common language since English is included by default.
  language: 'es-US',
  speakDatesAs: null,
  voice: ''
};

type LanguageFormProps = Partial<PopUpProps> & {
  audioType?: AudioType;
  deletable?: boolean;
  labels?: Record<AudioType | 'language', string>;
  language?: DesignLanguage;
  onSave: (language: DesignLanguage, action: 'update' | 'delete') => void;
};

export const LanguageForm: FC<LanguageFormProps> = ({
  children,
  deletable = true,
  labels = null,
  language: initialLanguage,
  onSave
}) => {
  const { globalModalsService, workflowService } = useServices();
  const [updatedLanguage, setUpdatedLanguage] = useState({ ...(initialLanguage ?? designLanguageSeed) });

  const { language: languageCode } = updatedLanguage || {};

  const [setRef, refRect, updateRect] = useRect();
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    updateRect();
  }, [isOpen]);

  const [languageOptions] = useAsync(() => workflowService.getLanguages(), []);
  const [vtOptions] = useAsync(() =>
    workflowService.getVoices(AudioType.ttp, languageCode)
  , [languageCode]);
  const [ttsOptions] = useAsync(() =>
    workflowService.getVoices(AudioType.tts, languageCode)
  , [languageCode]);

  const vtOptionsWithNone = useMemo(() => {
    if (!vtOptions) {
      return null;
    }
    return [
      { label: '<None>', value: '' },
      ...vtOptions
    ];
  }, [vtOptions]);

  const ttsOptionsWithNone = useMemo(() => {
    if (!ttsOptions) {
      return null;
    }
    return [
      { label: '<None>', value: '' },
      ...ttsOptions
    ];
  }, [ttsOptions]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!updatedLanguage.language) {
      setErrorMessage('Please select a language');
      return;
    }
    if (!updatedLanguage.voice && !updatedLanguage.ttsFallBackVoice) {
      setErrorMessage('Please select at least a TTP or a TTS voice');
      return;
    }
    onSave(updatedLanguage, 'update');
    setIsOpen(false);
  };

  const onRemove = async () => {
    const confirm = await globalModalsService.openConfirmationModal({
      children: `Are you sure you want to delete "${labels?.language}"?`,
      labels: {
        header: 'Delete confirmation',
        cancelButton: 'Cancel',
        continueButton: 'Delete'
      }
    });
    if (!confirm) {
      return;
    }
    onSave(updatedLanguage!, 'delete');
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        ref={setRef as any}
      >
        {children}
      </button>
      {(isOpen) && (
        <PopUp
          className={styles.popUp}
          direction={Direction.right}
          handleClose={() => setIsOpen(false)}
          key={String(isOpen)}
          refRect={refRect}
          useBackdrop
        >
          <form
            onSubmit={onSubmit}
            onInput={() => setErrorMessage('')}
          >
            <div className={styles.form}>
              <Select<LanguageCode>
                label={languageTypeLabels.language}
                name="language"
                options={languageOptions ?? undefined}
                value={updatedLanguage.language ?? null}
                onChange={(value) => {
                  if (value) {
                    setUpdatedLanguage({ ...designLanguageSeed, language: value });
                  }
                }}
                isDisabled={!!initialLanguage?.language}
                isClearable={!initialLanguage?.language}
                placeholder="Select a language"
                noOptionsMessage={() => 'No language found'}
              />
              <Select<string>
                label={languageTypeLabels[AudioType.ttp]}
                name="voice"
                tooltip='Selecting "<None>" will result in a fully TTS language.'
                options={vtOptionsWithNone ?? undefined}
                value={updatedLanguage.voice ?? null}
                onChange={(value) => {
                  setUpdatedLanguage({ ...updatedLanguage, voice: (value ?? '') as Voice | '' });
                }}
                isDisabled={!languageCode}
                isClearable
                placeholder={languageCode ? 'Select a voice talent' : 'Select a language first'}
                noOptionsMessage={() => 'No voice talent found'}
              />
              <Select<string>
                label={languageTypeLabels[AudioType.tts]}
                name="ttsFallBackVoice"
                options={ttsOptionsWithNone ?? undefined}
                value={updatedLanguage.ttsFallBackVoice ?? null}
                onChange={(value) => {
                  setUpdatedLanguage({ ...updatedLanguage, ttsFallBackVoice: value as TextToSpeechVoice | undefined });
                }}
                isDisabled={!languageCode}
                isClearable
                placeholder={languageCode ? 'Select a TTS voice' : 'Select a language first'}
                noOptionsMessage={() => 'No TTS voice found'}
              />
              <Select<LanguageSpeakDatesAs | null>
                isDisabled={!languageCode}
                isClearable
                label={languageTypeLabels.ttsDateFormat}
                name="speakDatesAs"
                onChange={(value) => {
                  setUpdatedLanguage({ ...updatedLanguage, speakDatesAs: value });
                }}
                options={languageSpeakDatesAsOptions}
                placeholder="Format of dates read in this language."
                value={updatedLanguage.speakDatesAs ?? null}
              />
              {(!errorMessage) ? null : (
                <div className={styles.errorMessage}>
                  <i className="fe icon-alert-octagon" />
                  {errorMessage}
                </div>
              )}
              <div className={styles.actions}>
                {(!deletable) ? null : (
                  <Button
                    type="button"
                    theme="outlineHighlight"
                    onClick={onRemove}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  type="button"
                  theme="outline"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button disabled={!languageCode}>Save</Button>
              </div>
            </div>
          </form>
        </PopUp>
      )}
    </>
  );
};
